import { Shield, MapPin, Users, Building2, CheckCircle2, Eye, Award } from 'lucide-react';

const ABOUT_IMAGE = "https://images.unsplash.com/photo-1761891916823-a8e45da8a892?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmYW1pbHklMjBoYXBweSUyMGtleXMlMjBuZXclMjBob21lfGVufDB8fHx8MTc2OTE4NDg1Nnww&ixlib=rb-4.1.0&q=85";

const RERA_NUMBER = "CGRERA100518A000114";

const stats = [
  { value: '2018', label: 'Year of Inception' },
  { value: '21+', label: 'Projects Completed' },
  { value: '340+', label: 'Happy Families' },
  { value: '10+', label: 'Cities Covered' },
];

const coverageLocations = [
  'Bhilai', 'Durg', 'Utai', 'Ahiwara', 'Kumhari', 
  'Kurud', 'Kohka', 'Charoda', 'Anjora', 'Janjgir'
];

const visionCities = [
  'Raipur', 'Durg', 'Rajnandgaon', 'Bilaspur', 
  'Korba', 'Ambikapur', 'Raigarh', 'Dhamtari'
];

const approach = [
  'Clear title plots with full registry support',
  'RERA compliant documentation and processes',
  'Step-by-step guidance from site visit to registry',
  'Local expertise in Jamul-Bhilai property market',
  'Transparent pricing with no hidden costs',
  'Post-sale support and assistance',
];

export default function AboutPage() {
  return (
    <div className="pt-16 lg:pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-brand-rust to-brand-orange">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center text-white">
            <h1 className="font-heading text-4xl lg:text-5xl font-bold mb-4">
              About Shree Bajrang Developers
            </h1>
            <p className="text-white/90 text-lg max-w-2xl mx-auto">
              Your trusted partner for RERA registered plots and homes in the Jamul-Bhilai region since 2018
            </p>
          </div>
        </div>
      </section>

      {/* RERA Badge Banner */}
      <section className="bg-brand-gold/10 border-b border-brand-gold/30 py-4">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
            <Shield className="h-6 w-6 text-brand-rust" />
            <p className="text-gray-800 text-center">
              <span className="font-semibold text-brand-rust">C.G. RERA Registered Developer</span> – 
              Registration No.: <span className="font-mono font-medium">{RERA_NUMBER}</span>
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section-padding" data-testid="intro-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-orange-100 text-brand-rust px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="h-4 w-4" />
                <span>C.G. RERA Registered Developer</span>
              </div>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Building Dreams Since 2018
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Shree Bajrang Developers is a family-run real estate development company based in Jamul, Bhilai. 
                  Founded in 2018, we have been dedicated to providing affordable yet planned residential plots 
                  and ready homes to families in the Chhattisgarh region.
                </p>
                <p className="bg-gradient-to-r from-orange-50 to-transparent p-4 rounded-lg border-l-4 border-brand-orange">
                  <strong className="text-brand-rust">Our Track Record:</strong> Shree Bajrang Developers has successfully 
                  worked on <span className="font-semibold text-gray-900">21+ projects</span> across locations such as{' '}
                  {coverageLocations.join(', ')}, serving over <span className="font-semibold text-gray-900">340+ happy families</span>.
                </p>
                <p>
                  As a C.G. RERA registered developer, we prioritize transparency, legal compliance, and customer 
                  satisfaction in every project we undertake. Our deep understanding of the local market and 
                  commitment to quality has made us a trusted name in the Jamul-Bhilai real estate sector.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={ABOUT_IMAGE} 
                  alt="Happy Family with New Home" 
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-brand-orange to-brand-rust rounded-full flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-2xl text-gray-900">7+</p>
                    <p className="text-gray-500 text-sm">Years Experience</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="text-center p-6 bg-white rounded-xl border border-gray-100"
              >
                <p className="font-heading text-4xl font-bold text-gradient mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-600 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Vision Section */}
      <section className="section-padding bg-gradient-to-br from-brand-rust to-brand-orange" data-testid="vision-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Eye className="h-4 w-4" />
              <span>Our Vision</span>
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-6">
              Expanding Horizons Across Chhattisgarh
            </h2>
            <p className="text-white/90 text-lg leading-relaxed mb-8">
              Our vision is to provide affordable and premium plots and residences in cities like{' '}
              <span className="font-semibold">{visionCities.join(', ')}</span> over the next 5 years.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {visionCities.map((city, index) => (
                <span 
                  key={index}
                  className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm border border-white/20"
                >
                  {city}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="section-padding" data-testid="approach-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Our Approach
              </h2>
              <p className="text-gray-600 mb-8">
                We believe in making the property buying process simple, transparent, and stress-free. 
                Here's what sets us apart:
              </p>
              <ul className="space-y-4">
                {approach.map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-brand-orange flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-brand-orange to-brand-rust rounded-2xl p-6 text-white">
                <MapPin className="h-10 w-10 mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2">10+ Locations</h3>
                <p className="text-white/80 text-sm">
                  Projects across Bhilai, Durg, Utai, Ahiwara, Kumhari & more
                </p>
              </div>
              <div className="bg-gray-100 rounded-2xl p-6">
                <Shield className="h-10 w-10 text-brand-orange mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2 text-gray-900">RERA Certified</h3>
                <p className="text-gray-600 text-sm">
                  Reg. No.: {RERA_NUMBER}
                </p>
              </div>
              <div className="bg-gray-100 rounded-2xl p-6">
                <Users className="h-10 w-10 text-brand-orange mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2 text-gray-900">340+ Families</h3>
                <p className="text-gray-600 text-sm">
                  Happy homeowners across Chhattisgarh
                </p>
              </div>
              <div className="bg-brand-gold/20 rounded-2xl p-6">
                <Award className="h-10 w-10 text-brand-rust mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2 text-gray-900">21+ Projects</h3>
                <p className="text-gray-600 text-sm">
                  Successfully delivered across the region
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gray-900 text-white" data-testid="cta-section">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl font-bold mb-4">
            Ready to Discuss Your Property Needs?
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Visit our office in Shivpuri, Jamul or give us a call to schedule a site visit
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="tel:7581061649"
              className="bg-gradient-to-r from-brand-orange to-brand-rust text-white rounded-full px-8 py-3 font-medium hover:shadow-lg transition-shadow"
            >
              Call: 7581061649
            </a>
            <a 
              href="https://wa.me/9131709809"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 text-white rounded-full px-8 py-3 font-medium hover:bg-green-600 transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
